// src/pages/DriverManagementPage.js
import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  BsPencilSquare,
  BsPhoneFill,
  BsPlusCircle,
  BsTelephoneFill,
  BsTrash,
} from "react-icons/bs";
import api from "../api";
import { AuthContext } from "../AuthContext";

const DOW_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const DRIVER_TYPE_OPTIONS = ["MALE", "FEMALE", "COUPLE"];

const PHONE_TYPE_OPTIONS = [
  { value: "MOBILE", label: "Mobile" },
  { value: "LANDLINE", label: "Landline" },
];

// A COUPLE driver is two people, so each extra number records whose it is.
const phoneNeedsName = (driverType) => driverType === "COUPLE";

function PhoneTypeIcon({ type, className }) {
  return type === "LANDLINE" ? (
    <BsTelephoneFill className={className} title="Landline" />
  ) : (
    <BsPhoneFill className={className} title="Mobile" />
  );
}

const emptyPhoneDraft = { name: "", type: "MOBILE", phone_number: "" };

const emptyDriverForm = {
  display_name: "",
  driver_type: "MALE",
  phone: "",
  email: "",
  active: true,
  once_per_day: false,
  notes: "",
  phones: [],
  weekly_availability: [],
  // Login account: "new" auto-creates a login; "existing" links a user account.
  account_mode: "new",
  link_user_id: "",
};

// Backend stores/returns naive ISO datetimes (e.g. "2026-06-25T14:00:00").
// <input type="datetime-local"> wants "YYYY-MM-DDTHH:MM".
const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : "");

function formatUnavailRange(u) {
  const fmt = (s) => {
    if (!s) return "?";
    const d = new Date(s);
    return Number.isNaN(d.getTime())
      ? s
      : d.toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
  };
  return `${fmt(u.start_datetime)} – ${fmt(u.end_datetime)}`;
}

function UnavailabilityEditor({ rows, onChange }) {
  const addRow = () =>
    onChange([...rows, { start_datetime: "", end_datetime: "", reason: "" }]);

  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  const updateRow = (idx, field, value) =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  return (
    <div>
      {rows.length === 0 ? (
        <div className="text-muted mb-2" style={{ fontSize: "0.9em" }}>
          No unavailability set. Add a period below.
        </div>
      ) : (
        <Table size="sm" bordered className="mb-2">
          <thead>
            <tr>
              <th>Start</th>
              <th>End</th>
              <th>Reason</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx}>
                <td>
                  <Form.Control
                    size="sm"
                    type="datetime-local"
                    value={r.start_datetime}
                    onChange={(e) =>
                      updateRow(idx, "start_datetime", e.target.value)
                    }
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    type="datetime-local"
                    value={r.end_datetime}
                    onChange={(e) =>
                      updateRow(idx, "end_datetime", e.target.value)
                    }
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    type="text"
                    value={r.reason}
                    placeholder="e.g. Vacation"
                    onChange={(e) => updateRow(idx, "reason", e.target.value)}
                  />
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeRow(idx)}
                  >
                    &times;
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Button size="sm" variant="outline-secondary" onClick={addRow}>
        + Add Unavailability Period
      </Button>
    </div>
  );
}

function formatAvailability(slots) {
  if (!slots || slots.length === 0) return <span className="text-muted">None</span>;
  return slots.map((s, i) => (
    <div key={i} style={{ fontSize: "0.85em" }}>
      {DOW_LABELS[s.day_of_week]}: {s.start_time} – {s.end_time}
    </div>
  ));
}

function AvailabilityEditor({ slots, onChange }) {
  const addSlot = () =>
    onChange([
      ...slots,
      { day_of_week: 1, start_time: "08:00", end_time: "17:00" },
    ]);

  const removeSlot = (idx) =>
    onChange(slots.filter((_, i) => i !== idx));

  const updateSlot = (idx, field, value) =>
    onChange(
      slots.map((s, i) =>
        i === idx ? { ...s, [field]: field === "day_of_week" ? Number(value) : value } : s
      )
    );

  return (
    <div>
      {slots.length === 0 ? (
        <div className="text-muted mb-2" style={{ fontSize: "0.9em" }}>
          No availability set. Add a slot below.
        </div>
      ) : (
        <Table size="sm" bordered className="mb-2">
          <thead>
            <tr>
              <th>Day</th>
              <th>Start</th>
              <th>End</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {slots.map((s, idx) => (
              <tr key={idx}>
                <td>
                  <Form.Select
                    size="sm"
                    value={s.day_of_week}
                    onChange={(e) => updateSlot(idx, "day_of_week", e.target.value)}
                  >
                    {DOW_LABELS.map((label, d) => (
                      <option key={d} value={d}>
                        {label}
                      </option>
                    ))}
                  </Form.Select>
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    type="time"
                    value={s.start_time}
                    onChange={(e) => updateSlot(idx, "start_time", e.target.value)}
                  />
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    type="time"
                    value={s.end_time}
                    onChange={(e) => updateSlot(idx, "end_time", e.target.value)}
                  />
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeSlot(idx)}
                  >
                    &times;
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Button size="sm" variant="outline-secondary" onClick={addSlot}>
        + Add Availability Slot
      </Button>
    </div>
  );
}

export default function DriverManagementPage() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.roles?.includes("ADMIN") || user?.roles?.includes("DIRECTOR");
  // Coordinators can add drivers, but deleting stays admin-only.
  const canAddDriver = isAdmin || user?.roles?.includes("COORDINATOR");

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [driverForm, setDriverForm] = useState(emptyDriverForm);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");
  const [linkUsers, setLinkUsers] = useState([]);

  // Phone sub-modal (edits driverForm.phones in place; saved with the driver)
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState(emptyPhoneDraft);
  const [phoneEditIndex, setPhoneEditIndex] = useState(null);
  const [phoneError, setPhoneError] = useState("");

  const openAddPhone = () => {
    setPhoneDraft(emptyPhoneDraft);
    setPhoneEditIndex(null);
    setPhoneError("");
    setShowPhoneModal(true);
  };

  const openEditPhone = (index) => {
    const p = driverForm.phones[index];
    setPhoneDraft({
      name: p.name || "",
      type: p.type || "MOBILE",
      phone_number: p.phone_number || "",
    });
    setPhoneEditIndex(index);
    setPhoneError("");
    setShowPhoneModal(true);
  };

  const savePhoneDraft = () => {
    const number = phoneDraft.phone_number.trim();
    if (!number) {
      setPhoneError("Phone number is required.");
      return;
    }
    if (phoneNeedsName(driverForm.driver_type) && !phoneDraft.name.trim()) {
      setPhoneError("Name is required — tell us whose number this is.");
      return;
    }
    const row = {
      name: phoneNeedsName(driverForm.driver_type) ? phoneDraft.name.trim() : null,
      type: phoneDraft.type,
      phone_number: number,
    };
    setDriverForm((prev) => {
      const phones = [...prev.phones];
      if (phoneEditIndex === null) phones.push(row);
      else phones[phoneEditIndex] = { ...phones[phoneEditIndex], ...row };
      return { ...prev, phones };
    });
    setShowPhoneModal(false);
  };

  const removePhone = (index) => {
    setDriverForm((prev) => ({
      ...prev,
      phones: prev.phones.filter((_, i) => i !== index),
    }));
  };

  const loadLinkableUsers = async () => {
    try {
      const res = await api.get("/drivers/linkable-users");
      setLinkUsers(res.data.items || []);
    } catch {
      setLinkUsers([]);
    }
  };

  // Delete confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingDriver, setDeletingDriver] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Unavailability modal
  const [showUnavailModal, setShowUnavailModal] = useState(false);
  const [unavailDriver, setUnavailDriver] = useState(null);
  const [unavailRows, setUnavailRows] = useState([]);
  const [unavailSaving, setUnavailSaving] = useState(false);
  const [unavailError, setUnavailError] = useState("");

  const loadDrivers = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/drivers", {
        // Must send an explicit "false" — omitting the param makes the API
        // fall back to its active-only default.
        params: { active: showInactive ? "false" : "true" },
      });
      setDrivers(res.data.items || []);
    } catch (e) {
      setError("Failed to load drivers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const openAdd = () => {
    setEditingDriver(null);
    setDriverForm({ ...emptyDriverForm, phones: [], weekly_availability: [] });
    setModalError("");
    loadLinkableUsers();
    setShowModal(true);
  };

  const openEdit = (driver) => {
    setEditingDriver(driver);
    setDriverForm({
      display_name: driver.display_name || "",
      driver_type: driver.driver_type || "MALE",
      phone: driver.phone || "",
      email: driver.email || "",
      active: driver.active !== false,
      once_per_day: driver.once_per_day || false,
      notes: driver.notes || "",
      phones: (driver.phones || []).map((p) => ({
        id: p.id,
        name: p.name || "",
        type: p.type || "MOBILE",
        phone_number: p.phone_number || "",
      })),
      weekly_availability: (driver.weekly_availability || []).map((wa) => ({
        day_of_week: wa.day_of_week,
        start_time: wa.start_time,
        end_time: wa.end_time,
      })),
      account_mode: "new", // edit form uses link_user_id only to *change* the link
      link_user_id: "",
    });
    setModalError("");
    loadLinkableUsers();
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!driverForm.display_name.trim()) {
      setModalError("Display name is required.");
      return;
    }
    // Linking an existing account requires a selection.
    if (driverForm.account_mode === "existing" && !driverForm.link_user_id) {
      setModalError("Select the existing user account to link, or choose to create a new login.");
      return;
    }
    setModalError("");
    setSaving(true);

    const payload = {
      display_name: driverForm.display_name.trim(),
      driver_type: driverForm.driver_type,
      phone: driverForm.phone || null,
      email: driverForm.email || null,
      active: driverForm.active,
      once_per_day: driverForm.once_per_day,
      notes: driverForm.notes || null,
      phones: driverForm.phones.map((p) => ({
        name: p.name || null,
        type: p.type,
        phone_number: p.phone_number,
      })),
      weekly_availability: driverForm.weekly_availability,
    };

    // Create: link existing account when chosen (else backend auto-creates a login).
    // Edit: only send user_id when relinking to a different account.
    if (!editingDriver && driverForm.account_mode === "existing") {
      payload.user_id = Number(driverForm.link_user_id);
    } else if (editingDriver && driverForm.link_user_id) {
      payload.user_id = Number(driverForm.link_user_id);
    }

    try {
      if (editingDriver) {
        await api.put(`/drivers/${editingDriver.id}`, payload);
      } else {
        await api.post("/drivers", payload);
      }
      setShowModal(false);
      await loadDrivers();
    } catch (e) {
      setModalError(e?.response?.data?.error || "Failed to save driver.");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (driver) => {
    setDeletingDriver(driver);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!deletingDriver) return;
    setDeleting(true);
    try {
      await api.delete(`/drivers/${deletingDriver.id}`);
      setShowDeleteConfirm(false);
      setDeletingDriver(null);
      await loadDrivers();
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          "Failed to delete driver. They may have ride assignments."
      );
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const openUnavailability = (driver) => {
    setUnavailDriver(driver);
    setUnavailRows(
      (driver.unavailability || []).map((u) => ({
        start_datetime: toLocalInput(u.start_datetime),
        end_datetime: toLocalInput(u.end_datetime),
        reason: u.reason || "",
      }))
    );
    setUnavailError("");
    setShowUnavailModal(true);
  };

  const handleSaveUnavailability = async () => {
    // Drop fully-empty rows; require both dates and start < end on the rest.
    const rows = unavailRows.filter(
      (r) => r.start_datetime || r.end_datetime || r.reason
    );
    for (const r of rows) {
      if (!r.start_datetime || !r.end_datetime) {
        setUnavailError("Each period needs both a start and an end.");
        return;
      }
      if (r.start_datetime >= r.end_datetime) {
        setUnavailError("Start must be before end for every period.");
        return;
      }
    }
    setUnavailError("");
    setUnavailSaving(true);
    try {
      await api.put(`/drivers/${unavailDriver.id}`, {
        unavailability: rows.map((r) => ({
          start_datetime: r.start_datetime,
          end_datetime: r.end_datetime,
          reason: r.reason.trim() || null,
        })),
      });
      setShowUnavailModal(false);
      setUnavailDriver(null);
      await loadDrivers();
    } catch (e) {
      setUnavailError(
        e?.response?.data?.error || "Failed to save unavailability."
      );
    } finally {
      setUnavailSaving(false);
    }
  };

  const setField = (field, value) =>
    setDriverForm((p) => ({ ...p, [field]: value }));

  const displayedDrivers = showInactive
    ? drivers
    : drivers.filter((d) => d.active);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Driver Management</h3>
        <div className="d-flex gap-2 align-items-center">
          <Form.Check
            type="switch"
            id="show-inactive"
            label="Show inactive"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          {canAddDriver && (
            <Button variant="primary" onClick={openAdd}>
              Add Driver
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Card>
          <Card.Body className="p-0">
            <Table striped bordered hover className="mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Type</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Weekly Availability</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedDrivers.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted">
                      No drivers found.
                    </td>
                  </tr>
                )}
                {displayedDrivers.map((d) => (
                  <tr key={d.id}>
                    <td className="fw-semibold">{d.display_name}</td>
                    <td>
                      {d.username ? (
                        <code style={{ fontSize: "0.85em" }}>{d.username}</code>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      <Badge
                        bg={
                          d.driver_type === "MALE"
                            ? "primary"
                            : d.driver_type === "FEMALE"
                            ? "pink"
                            : "success"
                        }
                        style={d.driver_type === "FEMALE" ? { backgroundColor: "#d63384" } : {}}
                      >
                        {d.driver_type}
                      </Badge>
                    </td>
                    <td className="text-nowrap">
                      <div>{d.phone || "—"}</div>
                      {(d.phones || []).map((p) => (
                        <div key={p.id} className="small">
                          <PhoneTypeIcon type={p.type} className="text-secondary me-1" />
                          {p.phone_number}
                          {p.name ? <span className="text-muted"> — {p.name}</span> : null}
                        </div>
                      ))}
                    </td>
                    <td>{d.email || "—"}</td>
                    <td>{formatAvailability(d.weekly_availability)}</td>
                    <td>
                      <Badge bg={d.active ? "success" : "secondary"}>
                        {d.active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openEdit(d)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openUnavailability(d)}
                        >
                          Unavailability
                          {d.unavailability && d.unavailability.length > 0 && (
                            <Badge bg="warning" text="dark" className="ms-1">
                              {d.unavailability.length}
                            </Badge>
                          )}
                        </Button>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => openDeleteConfirm(d)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Add / Edit Driver Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingDriver ? "Edit Driver" : "Add Driver"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalError && <Alert variant="danger">{modalError}</Alert>}
          <Form>
            <Row className="g-2 mb-3">
              <Col md={8}>
                <Form.Group>
                  <Form.Label>
                    Full Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={driverForm.display_name}
                    onChange={(e) => setField("display_name", e.target.value)}
                    placeholder="e.g. Jim Cobb"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    Driver Type <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={driverForm.driver_type}
                    onChange={(e) => setField("driver_type", e.target.value)}
                  >
                    {DRIVER_TYPE_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Used to match eligible riders.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-2 mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Login account</Form.Label>
                  {editingDriver ? (
                    <>
                      <div className="small text-muted mb-1">
                        Current login:{" "}
                        {editingDriver.username ? (
                          <strong>{editingDriver.username}</strong>
                        ) : (
                          <em>none linked</em>
                        )}
                      </div>
                      <Form.Select
                        value={driverForm.link_user_id}
                        onChange={(e) => setField("link_user_id", e.target.value)}
                      >
                        <option value="">— Keep current login —</option>
                        {linkUsers.map((u) => (
                          <option
                            key={u.id}
                            value={u.id}
                            disabled={!!u.linked_driver_id && u.linked_driver_id !== editingDriver.id}
                          >
                            {u.full_name} ({u.username})
                            {u.linked_driver_id && u.linked_driver_id !== editingDriver.id
                              ? ` — already linked to ${u.linked_driver_name}`
                              : ""}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Choose a different account only to re-link this driver.
                      </Form.Text>
                    </>
                  ) : (
                    <>
                      <Form.Check
                        type="radio"
                        id="acct-new"
                        name="account_mode"
                        label="Create a new login for this driver"
                        checked={driverForm.account_mode === "new"}
                        onChange={() => setField("account_mode", "new")}
                      />
                      <Form.Check
                        type="radio"
                        id="acct-existing"
                        name="account_mode"
                        label="Link an existing user account"
                        checked={driverForm.account_mode === "existing"}
                        onChange={() => setField("account_mode", "existing")}
                      />
                      {driverForm.account_mode === "new" ? (
                        <Form.Text className="text-muted">
                          Username auto-generated as first initial + last name (e.g.{" "}
                          <em>jcobb</em>); the driver changes the temporary password on first login.
                        </Form.Text>
                      ) : (
                        <Form.Select
                          className="mt-2"
                          value={driverForm.link_user_id}
                          onChange={(e) => setField("link_user_id", e.target.value)}
                        >
                          <option value="">— Select a user —</option>
                          {linkUsers.map((u) => (
                            <option key={u.id} value={u.id} disabled={!!u.linked_driver_id}>
                              {u.full_name} ({u.username})
                              {u.linked_driver_id ? " — already a driver" : ""}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    </>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-2 mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={driverForm.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={driverForm.email}
                    onChange={(e) => setField("email", e.target.value)}
                    placeholder="driver@example.com"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <Form.Label className="mb-0">Additional Phone Numbers</Form.Label>
                <Button size="sm" variant="outline-primary" onClick={openAddPhone}>
                  <BsPlusCircle className="me-1" />
                  Add Number
                </Button>
              </div>
              {driverForm.phones.length === 0 ? (
                <div className="small text-muted">
                  None. The phone above is this driver&apos;s primary number.
                </div>
              ) : (
                <Table size="sm" className="align-middle mb-0">
                  <tbody>
                    {driverForm.phones.map((p, i) => (
                      <tr key={p.id ?? `new-${i}`}>
                        <td style={{ width: "2rem" }}>
                          <PhoneTypeIcon type={p.type} className="text-secondary" />
                        </td>
                        <td>{p.phone_number}</td>
                        <td className="text-muted">
                          {phoneNeedsName(driverForm.driver_type) && p.name ? p.name : ""}
                        </td>
                        <td className="text-end" style={{ width: "5rem" }}>
                          <Button
                            size="sm"
                            variant="link"
                            className="p-0 me-2"
                            title="Edit"
                            onClick={() => openEditPhone(i)}
                          >
                            <BsPencilSquare />
                          </Button>
                          <Button
                            size="sm"
                            variant="link"
                            className="p-0 text-danger"
                            title="Remove"
                            onClick={() => removePhone(i)}
                          >
                            <BsTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                id="driver-active"
                label="Active (available for assignments)"
                checked={driverForm.active}
                onChange={(e) => setField("active", e.target.checked)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="driver-once-per-day"
                label="Limit to one ride per day"
                checked={driverForm.once_per_day}
                onChange={(e) => setField("once_per_day", e.target.checked)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={driverForm.notes}
                onChange={(e) => setField("notes", e.target.value)}
                placeholder="Vehicle type, special notes, etc."
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="fw-semibold">Weekly Availability</Form.Label>
              <AvailabilityEditor
                slots={driverForm.weekly_availability}
                onChange={(slots) => setField("weekly_availability", slots)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving && (
              <Spinner size="sm" animation="border" className="me-1" />
            )}
            {editingDriver ? "Save Changes" : "Add Driver"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Phone number sub-modal — edits the list in the driver form; the rows
          are written to driver_phones when the driver itself is saved. */}
      <Modal show={showPhoneModal} onHide={() => setShowPhoneModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5">
            {phoneEditIndex === null ? "Add Phone Number" : "Edit Phone Number"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {phoneError && <Alert variant="danger">{phoneError}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <div>
                {PHONE_TYPE_OPTIONS.map((o) => (
                  <Form.Check
                    key={o.value}
                    inline
                    type="radio"
                    id={`phone-type-${o.value}`}
                    name="phone_type"
                    checked={phoneDraft.type === o.value}
                    onChange={() => setPhoneDraft((p) => ({ ...p, type: o.value }))}
                    label={
                      <>
                        <PhoneTypeIcon type={o.value} className="me-1" />
                        {o.label}
                      </>
                    }
                  />
                ))}
              </div>
            </Form.Group>

            {phoneNeedsName(driverForm.driver_type) && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={phoneDraft.name}
                  onChange={(e) =>
                    setPhoneDraft((p) => ({ ...p, name: e.target.value }))
                  }
                  placeholder="Whose number is this?"
                />
              </Form.Group>
            )}

            <Form.Group>
              <Form.Label>
                Phone Number <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="tel"
                value={phoneDraft.phone_number}
                onChange={(e) =>
                  setPhoneDraft((p) => ({ ...p, phone_number: e.target.value }))
                }
                placeholder="(555) 555-5555"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowPhoneModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={savePhoneDraft}>
            {phoneEditIndex === null ? "Add" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Unavailability Modal */}
      <Modal
        show={showUnavailModal}
        onHide={() => setShowUnavailModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Unavailability — {unavailDriver?.display_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {unavailError && <Alert variant="danger">{unavailError}</Alert>}
          <p className="text-muted" style={{ fontSize: "0.9em" }}>
            Periods when this driver is unavailable for assignments (vacation,
            appointments, etc.). These block the driver from being matched to
            rides that overlap the range.
          </p>
          <UnavailabilityEditor rows={unavailRows} onChange={setUnavailRows} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowUnavailModal(false)}
            disabled={unavailSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveUnavailability}
            disabled={unavailSaving}
          >
            {unavailSaving && (
              <Spinner size="sm" animation="border" className="me-1" />
            )}
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete driver <strong>{deletingDriver?.display_name}</strong>? This
          cannot be undone. Drivers with existing ride assignments cannot be
          deleted.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteConfirm(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting && (
              <Spinner size="sm" animation="border" className="me-1" />
            )}
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
